import React from 'react';
import {
  APP_PRESENCE_FILTERS,
  DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
  DUTY_STATUS_FILTERS,
  GPS_STATUS_FILTERS,
  INCIDENT_READINESS_FILTERS,
  LAST_SEEN_WITHIN_FILTERS,
  MAX_DISTANCE_FILTERS,
  MAX_ETA_FILTERS,
} from './personnelAvailability';

const NUMERIC_FIELDS = new Set(['seenWithin', 'maxDistance', 'maxEta']);

const FilterGroup = ({
  label,
  field,
  options,
  selected,
  onSelect,
  dark,
  disabled,
  testIdPrefix,
}) => (
  <div className="flex min-w-0 flex-wrap items-center gap-1" data-testid={`${testIdPrefix}-${field}-filter`}>
    <span className={`mr-0.5 text-[9px] font-extrabold uppercase tracking-wide ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
      {label}
    </span>
    {options.map((option) => {
      const optionSelected = option.value === null
        ? selected === null || selected === undefined || selected === ''
        : NUMERIC_FIELDS.has(field)
          ? Number(selected) === option.value
          : selected === option.value;
      return (
        <button
          key={option.value === null ? 'any' : String(option.value)}
          type="button"
          disabled={disabled}
          aria-pressed={optionSelected}
          onClick={() => onSelect(field, option.value)}
          data-testid={`${testIdPrefix}-${field}-${option.value === null ? 'any' : option.value}`}
          className={`rounded-full border px-2 py-0.5 text-[9px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${optionSelected
            ? dark
              ? 'border-cyan-600 bg-cyan-600 text-white'
              : 'border-violet-600 bg-violet-600 text-white'
            : dark
              ? 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700'}`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export const PersonnelAvailabilityFilters = ({
  value = DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
  onChange = () => {},
  variant = 'light',
  showApp = true,
  showSeen = true,
  showDuty = true,
  showIncident = true,
  showGps = true,
  showDistance = true,
  showEta = true,
  disabled = false,
  testIdPrefix = 'availability',
  className = '',
}) => {
  const filters = { ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS, ...(value || {}) };
  const dark = variant === 'dark';
  const update = (field, nextValue) => onChange({ ...filters, [field]: nextValue });

  return (
    <div
      className={`flex flex-col gap-1.5 ${className}`}
      data-testid={`${testIdPrefix}-filters`}
      aria-label="Personnel availability filters"
    >
      {showApp && (
        <FilterGroup
          label="App"
          field="app"
          options={APP_PRESENCE_FILTERS}
          selected={filters.app}
          onSelect={update}
          dark={dark}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showSeen && (
        <FilterGroup
          label="Seen"
          field="seenWithin"
          options={LAST_SEEN_WITHIN_FILTERS}
          selected={filters.seenWithin}
          onSelect={update}
          dark={dark}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showDuty && (
        <FilterGroup
          label="Duty"
          field="duty"
          options={DUTY_STATUS_FILTERS}
          selected={filters.duty}
          onSelect={update}
          dark={dark}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showIncident && (
        <FilterGroup
          label="Incident"
          field="incident"
          options={INCIDENT_READINESS_FILTERS}
          selected={filters.incident}
          onSelect={update}
          dark={dark}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showGps && (
        <FilterGroup
          label="GPS"
          field="gps"
          options={GPS_STATUS_FILTERS}
          selected={filters.gps}
          onSelect={update}
          dark={dark}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showDistance && (
        <FilterGroup
          label="Distance"
          field="maxDistance"
          options={MAX_DISTANCE_FILTERS}
          selected={filters.maxDistance}
          onSelect={update}
          dark={dark}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showEta && (
        <FilterGroup
          label="ETA"
          field="maxEta"
          options={MAX_ETA_FILTERS}
          selected={filters.maxEta}
          onSelect={update}
          dark={dark}
          disabled={disabled}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
};

export default PersonnelAvailabilityFilters;
